import { takeLatest, put, call, all, select, takeLeading } from 'redux-saga/effects';
import get from 'lodash/get';
import { Router } from 'src/i18n';
import { getRouteWithCountryAndSlug, getRouteWithSlug } from 'utils/common';
import { REG_HBSPT_FORM_DATA, REGISTRATION_STEPS, GIFT_TOKEN_TYPES } from './constants';

import {
  initRegistrationAction,
  setActiveRegistrationsAction,
  updateRegistrationAction,
  createProfileAction,
  createAgencyProfileAction,
  createAosProfileAction,
  createArtistProfileAction,
  updateProfileAction,
  getRegistrationsAction,
  setRegistrationProfileAction,
  initRegistrationContactAction,
  createCompanyAction,
  createAgencyAction,
  chooseProfileTypeAction,
  updateUserInfoAction,
  setProfileAccessesAction,
  createGiftAccountAction,
  updateGiftProfileAction,
} from './actions';
import * as API from '../../../utils/API';
import { loginUser, updateActiveProfile, updateUserDataSaga } from '../../App/saga';
import { selectProSubscription, selectUserProfiles } from '../../App/selectors';
import route from '../../../constants/routes';
import { setUserProfiles, setUserData, enqueueSnackbarAction } from '../../App/actions';
import { getProductsAction } from '../../UserContainers/AccountSettingsContainer/actions';
import { setIsLoggedInAction } from '../../../store/actions';
import { selectRegistrations } from './selectors';
import { submitHubspotForm } from '../../../utils/formUtils';
import {
  AGENCIES,
  COMPANIES,
  COMPANY_EMPLOYEES,
  PROFILES_ENDPOINT,
  SET_PROFILE_ACCESS_REQUESTS,
} from '../../../constants/endpointConstants';
import { selectLanguage } from '../../../store/selectors';
import { bugsnagNotify } from '../../../utils/bugsnagClientWrapper';
import { ARTIST_TYPE_ID } from '../../../constants/consts';

export const sendRegFormDataToHbspt = ({ regStep, regToken, profileType, hbsptPayload = [], language }) => {
  if (!regStep) return;
  const portalId = process.env.HUBSPOT_REGISTER_KEY;
  const env = process.env.APP_ENV;
  const { formId, step } = REG_HBSPT_FORM_DATA(regStep, profileType, env);

  const languageIndex = hbsptPayload.indexOf(hbsptPayload.find(item => item.name === 'hs_language'));

  let selectedLanguage = null;

  if (languageIndex !== -1) {
    selectedLanguage = hbsptPayload.splice(languageIndex, 1)[0].value;
  }

  const payload = {
    fields: [
      { name: 'registration_form_step', value: step },
      {
        name: 'registration_link',
        value: `${window.location.protocol}//${window.location.host}${route.REGISTER}?regtoken=${regToken}`,
      },
      { name: 'hs_language', value: selectedLanguage || language },
      ...hbsptPayload,
    ],
  };
  if (regStep !== REGISTRATION_STEPS.payment && hbsptPayload.length !== 0 && formId) {
    submitHubspotForm(portalId, formId, payload);
  }
};

export function* initRegistration({ payload, serverCookies }) {
  try {
    const hbsptPayload = get(payload, 'hbsptPayload');
    let userData;
    const language = yield select(selectLanguage());
    if (get(payload, 'userRegData')) {
      const response = yield call(
        API.registerUserWithoutPassword,
        { ...get(payload, 'userRegData'), preferredLanguage: language },
        serverCookies,
      );
      userData = response?.data?.data;
      yield put(setUserData.success(userData));
      yield put(setIsLoggedInAction(true));

      hbsptPayload.push({
        name: 'user_id__database_',
        value: get(userData, 'id'),
      });
    }

    const createRegResponse = yield call(
      API.createRegistration,
      {
        ...get(payload, 'registration'),
        email: get(payload, 'registration.email', payload?.user?.email),
        user: get(payload, 'user', userData),
      },
      serverCookies,
    );
    const registrations = yield select(selectRegistrations());
    yield put(getRegistrationsAction.success({ data: [createRegResponse.data, ...registrations] }));
    yield put(setActiveRegistrationsAction.success(createRegResponse.data));

    try {
      if (get(payload, 'hbsptPayload')) {
        sendRegFormDataToHbspt({
          regStep: get(payload, 'currentStep'),
          regToken: createRegResponse.data.token,
          profileType: parseInt(get(payload, 'registration.type'), 0),
          hbsptPayload: get(payload, 'hbsptPayload'),
          language,
        });
      }
    } catch (err) {
      bugsnagNotify(err);
    }

    if (payload?.onSuccess) {
      payload.onSuccess();
    }
    if (payload?.trackStartRegistration) {
      payload.trackStartRegistration();
    }

    if (!payload?.withoutRedirect) {
      Router.push(route.REGISTER, `${route.REGISTER}?regtoken=${createRegResponse.data.token}`, {
        shallow: true,
      });
    }
  } catch (err) {
    yield put(setActiveRegistrationsAction.failure(err.message));
    if (payload?.onRejected) {
      payload.onRejected(err);
    }
  } finally {
    yield put(setActiveRegistrationsAction.fulfill());
    if (payload?.callback) {
      payload.callback();
    }
  }
}

export function* updateRegistration({ payload, serverCookies }) {
  try {
    const createRegResponse = yield call(
      API.updateRegistration,
      get(payload, 'registration.id'),
      get(payload, 'registration'),
      serverCookies,
    );

    const registrations = yield select(selectRegistrations());
    const updatedRegistrations = registrations.map(reg =>
      reg.id === createRegResponse?.data?.id ? createRegResponse.data : reg,
    );

    yield put(getRegistrationsAction.success({ data: updatedRegistrations }));
    yield put(setActiveRegistrationsAction.success(createRegResponse.data));

    const language = yield select(selectLanguage());

    if (get(payload, 'hbsptPayload')) {
      sendRegFormDataToHbspt({
        regStep: get(payload, 'currentStep'),
        regToken: createRegResponse.data.token,
        profileType: parseInt(get(payload, 'registration.type'), 0),
        hbsptPayload: get(payload, 'hbsptPayload'),
        language,
      });
    }

    const preferredLanguage = get(payload, `preferedLanguage`);

    if (preferredLanguage) {
      yield call(API.updateSettingsField, get(payload, 'registration.user.id'), 'lang', preferredLanguage);
    }

    if (payload?.onSuccess) {
      payload.onSuccess();
    }

    if (payload?.disableRedirect) {
      return;
    }

    if (!payload?.withoutRedirect) {
      Router.push(route.REGISTER, `${route.REGISTER}?regtoken=${createRegResponse.data.token}`, { shallow: true });
    }
  } catch (err) {
    yield put(setActiveRegistrationsAction.failure(err.message));
  } finally {
    yield put(setActiveRegistrationsAction.fulfill());
    if (payload?.callback) yield call(payload.callback);
  }
}

export function* setRegistration({ payload, serverCookies }) {
  const language = yield select(selectLanguage());
  let createRegResponse;
  if (get(payload, 'initRegistration')) {
    createRegResponse = yield call(
      API.createRegistration,
      {
        ...get(payload, 'registration'),
        email: get(payload, 'registration.email', payload?.user?.email),
        user: get(payload, 'user'),
      },
      serverCookies,
    );
  } else {
    createRegResponse = yield call(
      API.updateRegistration,
      get(payload, 'registration.id'),
      get(payload, 'registration'),
      serverCookies,
    );
  }

  try {
    const registrations = yield select(selectRegistrations());
    yield put(setActiveRegistrationsAction.success(createRegResponse.data));

    if (get(payload, 'initRegistration')) {
      yield put(getRegistrationsAction.success({ data: [createRegResponse.data, ...registrations] }));
    } else {
      const registrationsList = registrations.map(reg => {
        if (reg?.id === createRegResponse?.data?.id) {
          return createRegResponse?.data;
        }
        return reg;
      });
      yield put(getRegistrationsAction.success({ data: registrationsList }));
    }

    if (get(payload, 'hbsptPayload')) {
      sendRegFormDataToHbspt({
        regStep: get(payload, 'currentStep'),
        regToken: createRegResponse.data.token,
        profileType: parseInt(get(payload, 'registration.type'), 0),
        hbsptPayload: get(payload, 'hbsptPayload'),
        language,
      });
    }
  } catch (err) {
    bugsnagNotify(err);
  }

  if (payload?.onSuccess) {
    payload.onSuccess();
  }
}

export function* createProfile({ payload, serverCookies }) {
  const response = yield call(API.createUserProfile, payload.profile, serverCookies);

  yield put(createProfileAction.success(response.data));
  const userProfiles = yield select(selectUserProfiles());
  yield put(setUserProfiles.success([...userProfiles, response.data]));
  yield call(updateActiveProfile, { payload: { profileId: response.data.id, fromReg: true } });
  return response.data;
}

export function* getActivePaidSubscriptions({ query, serverCookies }) {
  const subscriptions = yield call(API.getSubscriptions, { query }, serverCookies);
  const activePaidSubscriptions = subscriptions?.data?.data?.filter(
    sub => sub?.status === 'active' && sub?.paymentType !== 'free',
  );
  return activePaidSubscriptions;
}

export function* redirectToPaymentPage({ profile, completeurl }) {
  const profileTypeId = profile.profileType.id;
  const proSubscription = yield select(selectProSubscription(profileTypeId));
  const URLParams = new URLSearchParams({
    subid: proSubscription.id,
    completeurl,
    profileid: profile.id,
  });
  window.location = `${route.PAYMENT}?${URLParams.toString()}`;
}

export function* createAosProfile({ payload, serverCookies }) {
  try {
    const profile = yield call(createProfile, { payload, serverCookies });

    const query = { company_id: profile.companies[0]?.company?.id };
    const activePaidSubscriptions = yield call(getActivePaidSubscriptions, { query, serverCookies });

    const completeurl = getRouteWithCountryAndSlug(route.COMPANIES, profile.companies[0].company);

    if (activePaidSubscriptions.length > 0) {
      yield call(setRegistration, {
        payload: {
          ...payload,
          registration: {
            ...get(payload, 'registration'),
            profile: { id: profile.id },
            step: REGISTRATION_STEPS.finished,
          },
        },
        serverCookies,
      });

      Router.push(completeurl);
    } else {
      yield call(get(payload, 'initRegistration') ? initRegistration : updateRegistration, {
        payload: {
          ...payload,
          registration: {
            ...get(payload, 'registration'),
            profile: { id: profile.id },
            step: REGISTRATION_STEPS.subscriptionPlan,
          },
        },
        serverCookies,
      });
    }
  } catch (err) {
    yield put(createProfileAction.failure(err.message));
    if (payload?.onRejected) {
      payload.onRejected();
    }
  } finally {
    yield put(createProfileAction.fulfill());
  }
}

export function* createAgencyProfile({ payload, serverCookies }) {
  try {
    const profile = yield call(createProfile, { payload, serverCookies });
    const query = { agency_id: profile.agencies[0]?.id };

    const activePaidSubscriptions = yield call(getActivePaidSubscriptions, { query, serverCookies });
    const completeurl = payload?.isNewAgency
      ? `${route.REGISTER}?regtoken=${get(payload, 'registration.token')}`
      : getRouteWithSlug(route.MANAGERS, profile.agencies[0]);

    if (activePaidSubscriptions.length > 0) {
      yield call(setRegistration, {
        payload: {
          ...payload,
          registration: {
            ...get(payload, 'registration'),
            profile: { id: profile.id },
            step: REGISTRATION_STEPS.finished,
          },
        },
        serverCookies,
      });

      Router.push(completeurl);
    } else {
      yield call(setRegistration, {
        payload: {
          ...payload,
          registration: {
            ...get(payload, 'registration'),
            profile: { id: profile.id },
            step: REGISTRATION_STEPS.payment,
          },
          withoutRedirect: true,
        },
        serverCookies,
      });
      yield call(redirectToPaymentPage, { profile, completeurl });
    }
  } catch (err) {
    yield put(createProfileAction.failure(err.message));
    if (payload?.onRejected) {
      payload.onRejected();
    }
  } finally {
    yield put(createProfileAction.fulfill());
  }
}

export function* createArtistProfile({ payload, serverCookies }) {
  try {
    const profile = yield call(createProfile, { payload, serverCookies });

    yield call(setRegistration, {
      payload: {
        ...payload,
        registration: {
          ...get(payload, 'registration'),
          profile: { id: profile.id },
          step: get(payload, 'registration.step'),
        },
        withoutRedirect: true,
      },
      serverCookies,
    });

    Router.push(route.REGISTER, `${route.REGISTER}?regtoken=${payload.registration.token}`, { shallow: true });
  } catch (err) {
    yield put(createProfileAction.failure(err.message));
    if (payload?.onRejected) {
      payload.onRejected();
    }
  } finally {
    yield put(createProfileAction.fulfill());
  }
}

export function* setProfileAccesses({ payload, serverCookies }) {
  try {
    const { profile, sender } = payload;
    yield call(
      API.create,
      SET_PROFILE_ACCESS_REQUESTS(profile?.id),
      { sender, message: 'registration' },
      {},
      serverCookies,
    );
    const userProfiles = yield select(selectUserProfiles());
    yield put(setUserProfiles.success([...userProfiles, profile]));
    yield call(updateActiveProfile, { payload: { profileId: profile.id, fromReg: true } });

    yield call(setRegistration, {
      payload: {
        ...payload,
        registration: {
          ...get(payload, 'registration'),
          profile: { id: profile.id },
          step: REGISTRATION_STEPS.digitalPresence,
        },
        hbsptPayload: get(payload, 'hbsptPayload'),
        currentStep: get(payload, 'currentStep'),
      },
      serverCookies,
    });

    if (payload?.onSuccess) {
      payload.onSuccess();
    }

    Router.push(route.REGISTER, `${route.REGISTER}?regtoken=${payload?.registration?.token}`, { shallow: true });
  } catch (err) {
    yield put(createProfileAction.failure(err.message));
    if (payload?.onRejected) {
      payload.onRejected();
    }
  } finally {
    yield put(createProfileAction.fulfill());
  }
}

export function* updateProfile({ payload, serverCookies }) {
  try {
    const endpoint = PROFILES_ENDPOINT;
    const { data: profile } = yield call(
      API.updateOne,
      endpoint,
      payload.profile.id,
      payload.profileUpdates,
      null,
      serverCookies,
    );
    yield put(updateProfileAction.success(profile));
    const userProfiles = yield select(selectUserProfiles());
    yield put(setUserProfiles.success(userProfiles.map(item => (item.id === profile.id ? profile : item))));
    yield call(updateActiveProfile, { payload: { profileId: profile.id, fromReg: true } });
    yield call(updateRegistration, { payload, serverCookies });
  } catch (err) {
    yield put(createProfileAction.failure(err.message));
  } finally {
    yield put(createProfileAction.fulfill());
  }
}

export function* restoreRegistration({ payload: { token } }) {
  try {
    const registrations = yield select(selectRegistrations());
    const userProfiles = yield select(selectUserProfiles());

    if (token) {
      const tokenRegistration = registrations.find(reg => reg.token && reg.token === token);

      if (tokenRegistration) {
        const activeProfile = userProfiles.find(profile => tokenRegistration?.profile?.id === profile?.id);
        const regProfile = activeProfile || tokenRegistration;

        yield put(setRegistrationProfileAction.success(regProfile));
        yield put(
          setActiveRegistrationsAction.success(
            tokenRegistration?.step === REGISTRATION_STEPS.payment && tokenRegistration?.type == ARTIST_TYPE_ID
              ? { ...tokenRegistration, step: REGISTRATION_STEPS.addingPerformances }
              : tokenRegistration,
          ),
        );
      }
    }
  } catch (error) {
    yield put(getRegistrationsAction.failure(error.message));
  } finally {
    yield put(getRegistrationsAction.fulfill());
  }
}

export function* registerContactForm({ payload, serverCookies }) {
  try {
    let registerUserRes;
    const hbsptPayload = get(payload, 'hbsptPayload');
    if (get(payload, 'userRegData')) {
      registerUserRes = yield call(API.registerUser, get(payload, 'userRegData'), serverCookies);
      hbsptPayload.push({
        name: 'user_id__database_',
        value: get(registerUserRes.data, 'id', ''),
      });
      yield loginUser({
        payload: {
          email: get(payload, 'userRegData.email'),
          password: get(payload, 'userRegData.password'),
          isTrustedDevice: true,
        },
        isAfterRegister: true,
      });
      yield put(setUserData.success(registerUserRes.data));
      yield put(setIsLoggedInAction(true));
    }

    const createProfileRes = yield call(
      API.createUserProfile,
      {
        ...payload.profile,
        ...(registerUserRes && { user: { id: get(registerUserRes, 'data.id') } }),
      },
      serverCookies,
    );
    yield put(createProfileAction.success(createProfileRes.data));
    const userProfiles = yield select(selectUserProfiles());
    yield put(setUserProfiles.success([...userProfiles, createProfileRes.data]));
    yield call(updateActiveProfile, { payload: { profileId: createProfileRes.data.id, fromReg: true } });

    const createRegRes = yield call(
      API.createRegistration,
      {
        ...get(payload, 'registration'),
        profile: { id: createProfileRes?.data?.id },
        user: get(payload, 'user') || registerUserRes.data,
      },
      serverCookies,
    );

    const registrations = yield select(selectRegistrations());
    yield put(getRegistrationsAction.success({ data: [createRegRes.data, ...registrations] }));
    yield put(setActiveRegistrationsAction.success(createRegRes.data));
    const language = yield select(selectLanguage());

    sendRegFormDataToHbspt({
      regStep: REGISTRATION_STEPS.createFreeProfile,
      regToken: createRegRes.data.token,
      profileType: parseInt(get(payload, 'registration.type'), 0),
      hbsptPayload,
      language,
    });

    if (payload?.onSuccess) {
      payload.onSuccess();
    }

    Router.push(route.REGISTER, `${route.REGISTER}?regtoken=${createRegRes.data.token}`, { shallow: true });
  } catch (err) {
    yield put(setActiveRegistrationsAction.failure(err.message));
    if (payload?.onRejected) {
      payload.onRejected(err);
    }
  } finally {
    yield put(setActiveRegistrationsAction.fulfill());
  }
}

export function* chooseProfileType({ payload, serverCookies }) {
  try {
    const initRegistrationCheck = get(payload, 'initRegistration');
    const initRegistrationData = get(payload, 'initRegistrationData');
    const updateRegistrationData = get(payload, 'updateRegistrationData');

    const registrationProcess = initRegistrationCheck ? initRegistration : updateRegistration;
    const registration = initRegistrationCheck ? initRegistrationData : updateRegistrationData;
    yield call(registrationProcess, {
      payload: { ...payload, registration },
      serverCookies,
    });
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: err.message,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* updateUserInfo({ payload, serverCookies }) {
  try {
    const updateUserChanges = get(payload, 'updateUserData');
    yield call(updateUserDataSaga, { payload: updateUserChanges, serverCookies });
    yield call(setRegistration, {
      payload,
      serverCookies,
    });
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: err.message,
        options: {
          variant: 'error',
        },
      }),
    );
    if (payload?.onRejected) {
      payload.onRejected(err);
    }
  }
}

export function* createCompany({ payload, serverCookies }) {
  try {
    const { data: company } = yield call(API.create, COMPANIES, payload.company, serverCookies);
    yield call(createAosProfile, {
      payload: {
        ...payload,
        registration: { ...payload.registration, company: { id: company.id }, company_name: company.name },
        profile: {
          ...payload.profile,
          companies: [{ company: { id: company.id } }],
        },
      },
      serverCookies,
    });
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: err.message,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* createAgency({ payload, serverCookies }) {
  try {
    const { data: agency } = yield call(API.create, AGENCIES, payload.agency, serverCookies);
    yield call(createAgencyProfile, {
      payload: {
        ...payload,
        registration: { ...payload.registration, agency: { id: agency.id }, agency_name: agency.name },
        profile: {
          ...payload.profile,
          agencies: [{ id: agency.id, workEmail: get(payload.user, 'email') }],
        },
        isNewAgency: true,
      },
      serverCookies,
    });
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: err.message,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* createCompanyEmployees({ payload, serverCookies }) {
  try {
    const employees = get(payload, 'employees', []);
    const endpoint = COMPANY_EMPLOYEES(payload.companyId);
    yield all([
      ...employees.map(employee =>
        call(
          API.create,
          endpoint,
          {
            workEmail: { email: get(employee, 'email', '') },
            workPhones: [{ phone: get(employee, 'phone', '') }],
            profile: { firstName: get(employee, 'firstName', ''), lastName: get(employee, 'lastName', '') },
          },
          serverCookies,
        ),
      ),
    ]);
    yield call(updateRegistration, {
      payload,
      serverCookies,
    });
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: err.message,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* createGiftAccount({ payload, serverCookies }) {
  let referral;
  try {
    const language = yield select(selectLanguage());

    if (!payload?.isReferral && !payload.isAdminToken) {
      referral = yield call(API.createReferralStatus, payload?.referral, serverCookies);
    }

    const response = yield call(
      API.registerUserWithoutPassword,
      { ...get(payload, 'userRegData'), preferredLanguage: language },
      serverCookies,
    );
    const userData = response?.data?.data;
    yield put(setUserData.success(userData));
    yield put(setIsLoggedInAction(true));

    if (payload?.isReferral || payload.isAdminToken) {
      yield call(
        API.updateOne,
        PROFILES_ENDPOINT,
        payload.profile.id,
        { user: { id: userData.id }, transferOwnership: false },
        null,
        serverCookies,
      );
      const userProfiles = yield select(selectUserProfiles());
      yield put(setUserProfiles.success([...userProfiles, payload.profile]));
      yield call(updateActiveProfile, { payload: { profileId: payload.profile.id, fromReg: true } });
      yield call(setRegistration, { payload: { ...payload, user: userData }, serverCookies });

      const completeurl = getRouteWithSlug(route.ARTISTS, payload.profile);

      if (payload?.isReferral) {
        Router.push(`${route.ARTISTS}/[id]/[[...action]]`, completeurl, { shallow: true });
      }

      if (payload.isAdminToken) {
        Router.push(route.REGISTER, `${route.REGISTER}?regtoken=${payload.registration.token}`, { shallow: true });
      }
    } else {
      yield call(setRegistration, {
        payload: {
          ...payload,
          user: userData,
          registration: {
            ...get(payload, 'registration'),
            gift_token: get(referral, 'data.token'),
            gift_token_type: GIFT_TOKEN_TYPES.USER,
          },
        },
        serverCookies,
      });

      Router.push(route.REGISTER, `${route.REGISTER}?regtoken=${payload.registration.token}`, { shallow: true });
    }

    if (payload?.onSuccess) {
      payload.onSuccess();
    }
  } catch (err) {
    if (payload?.onRejected) {
      payload.onRejected(err);
    }
  }
}

export function* updateGiftProfile({ payload, serverCookies }) {
  try {
    if (payload?.isReferral || payload?.isAdminToken) {
      const { data: profile } = yield call(
        API.updateOne,
        PROFILES_ENDPOINT,
        payload.profile.id,
        payload.profileUpdates,
        null,
        serverCookies,
      );
      yield call(updateActiveProfile, { payload: { profileId: profile.id, fromReg: true } });
      const response = yield call(API.getProductsList, serverCookies);
      yield put(getProductsAction.success(get(response, 'data', [])));
      yield call(setRegistration, { payload, serverCookies });
    } else {
      const profile = yield call(createProfile, { payload, serverCookies });
      const referralPayload = { receiver: { profile: { id: profile.id } }, skipEmail: true };
      yield call(
        API.updateReferralStatus,
        get(payload, 'referral.senderUserId'),
        get(payload, 'referral.id'),
        referralPayload,
        serverCookies,
      );
      yield call(updateActiveProfile, { payload: { profileId: profile.id, fromReg: true } });
      yield call(setRegistration, {
        payload: {
          ...payload,
          registration: {
            ...get(payload, 'registration'),
            profile: { id: profile.id },
            gift_token_type: GIFT_TOKEN_TYPES.GIFT,
          },
        },
        serverCookies,
      });

      Router.push(route.REGISTER, `${route.REGISTER}?regtoken=${payload.registration.token}`, { shallow: true });
    }
  } catch (err) {
    bugsnagNotify(err);
  }
}
export default function* registrationSaga() {
  yield takeLeading(initRegistrationAction.TRIGGER, initRegistration);
  yield takeLatest(updateRegistrationAction.TRIGGER, updateRegistration);
  yield takeLatest(createAosProfileAction.TRIGGER, createAosProfile);
  yield takeLatest(createAgencyProfileAction.TRIGGER, createAgencyProfile);
  yield takeLatest(createArtistProfileAction.TRIGGER, createArtistProfile);
  yield takeLatest(setProfileAccessesAction.TRIGGER, setProfileAccesses);
  yield takeLatest(updateProfileAction.TRIGGER, updateProfile);
  yield takeLatest(getRegistrationsAction.TRIGGER, restoreRegistration);
  yield takeLatest(initRegistrationContactAction.TRIGGER, registerContactForm);
  yield takeLatest(createCompanyAction.TRIGGER, createCompany);
  yield takeLatest(createAgencyAction.TRIGGER, createAgency);
  yield takeLatest(chooseProfileTypeAction.TRIGGER, chooseProfileType);
  yield takeLatest(updateUserInfoAction.TRIGGER, updateUserInfo);
  yield takeLatest(createGiftAccountAction.TRIGGER, createGiftAccount);
  yield takeLatest(updateGiftProfileAction.TRIGGER, updateGiftProfile);
}
